/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo } from 'react';
import {
  Linking,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
  ActivityIndicator,
} from 'react-native';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { ticketColors } from '../../shared/theme/ticketColors';
import { settingsStyles } from '../../settings/screens/settingsScreen.styles';
import { formatMoney } from '../../shared/format/public-api';
import { StatusPill } from '../../shared/surface-cards/StatusPill';
import { EmbeddedMetric } from '../../shared/surface-cards/EmbeddedMetric'
import { EmbeddedMetricGrid } from '../../shared/surface-cards/EmbeddedMetricGrid';
import {
  formatCuitDisplay,
  MONOTRIBUTO_OFFICIAL_CATEGORIES_URL,
  monotributoLimitForCategory,
  spanishArcaEnvironmentLabel,
  spanishTaxRegimeLabel,
} from '../domain/fiscalPresentation';
import type { BranchFiscalSettings, FiscalProfile } from '../domain/fiscalTypes';

const WIDE_BREAKPOINT = 768;

export type FiscalStatusCardProps = {
  profile: FiscalProfile | null;
  branchFiscal?: BranchFiscalSettings | null;
  ytdTotal: number | null;
  /** Desglose YTD de la sucursal activa (informativo en mono multi-local). */
  ytdBranchTotal?: number | null;
  loading?: boolean;
  showActions?: boolean;
  onEditConfiguration?: () => void;
  onChangeRegime?: () => void;
  onViewAllBranches?: () => void;
  /** Exportación libro IVA (RI) — la fila de acciones solo se renderiza si hay algún callback. */
  onExportLibroIva?: () => void;
  /** Modo prueba → abre el wizard para facturar de verdad. */
  onActivateProduction?: () => void;
  branchId?: string | null;
  /** 'compact' fuerza layout apilado en contenedores estrechos; 'auto' usa ancho de ventana. */
  layoutMode?: 'auto' | 'compact';
};

/** `YYYY-MM-DD` → `DD/MM/AAAA` sin timezone shift (inicio de actividades). */
function formatIsoDateLabelEsAr(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
}

/** Proto YTD: `$3.240.500` / `de $10.277.988` — sin espacio raro ni cents forzados. */
const formatYtdMoney = formatMoney;

export function FiscalStatusCard({
  profile,
  branchFiscal,
  ytdTotal,
  ytdBranchTotal,
  loading = false,
  showActions = false,
  onEditConfiguration,
  onChangeRegime,
  onViewAllBranches,
  onExportLibroIva,
  onActivateProduction,
  branchId,
  layoutMode = 'auto',
}: FiscalStatusCardProps) {
  const { width } = useWindowDimensions();
  const wide = layoutMode === 'compact' ? false : width >= WIDE_BREAKPOINT;

  const isMono = profile?.taxRegime === 'monotributo';
  const limit = isMono ? monotributoLimitForCategory(profile?.monotributoCategory) : null;
  const limitPct =
    limit != null && ytdTotal != null && limit > 0 ? Math.min(100, Math.round((ytdTotal / limit) * 100)) : null;

  const validatedHint = useMemo(() => {
    if (!profile?.setupValidatedAt) return undefined;
    return new Date(profile.setupValidatedAt).toLocaleDateString('es-AR');
  }, [profile?.setupValidatedAt]);

  if (loading) {
    return (
      <View style={styles.loadingShell}>
        <ActivityIndicator color={ticketColors.gold} />
      </View>
    );
  }

  const orgStatusPill =
    profile?.setupStatus === 'validated' ? (
      <StatusPill label="Conectada" tone="success" iconName="checkmark-circle" compact />
    ) : profile?.setupStatus === 'error' ? (
      <StatusPill label="Error" tone="danger" iconName="alert-circle-outline" compact />
    ) : (
      <StatusPill label="Pendiente" tone="warning" iconName="time-outline" compact />
    );

  const pvStatusPill = branchFiscal?.pvValidatedAt ? (
    <StatusPill label="Este local listo" tone="success" iconName="shield-checkmark" compact />
  ) : branchFiscal?.pointOfSale != null ? (
    <StatusPill label="Este local sin verificar" tone="warning" iconName="time-outline" compact />
  ) : (
    <StatusPill label="Este local sin punto de venta" tone="danger" iconName="alert-circle-outline" compact />
  );

  const certExpiryPill = useMemo(() => {
    const exp = profile?.arcaCertExpiresAt;
    if (!exp) return null;
    const ms = new Date(exp).getTime();
    if (!Number.isFinite(ms)) return null;
    const days = Math.ceil((ms - Date.now()) / 86_400_000);
    if (days < 0) {
      return <StatusPill label="Certificado vencido" tone="danger" iconName="alert-circle-outline" compact />;
    }
    if (days <= 45) {
      return (
        <StatusPill
          label={`Cert. vence el ${new Date(ms).toLocaleDateString('es-AR')}`}
          tone="warning"
          iconName="time-outline"
          compact
        />
      );
    }
    return null;
  }, [profile?.arcaCertExpiresAt]);

  const metricCell = wide ? styles.metricCellWide : styles.metricCellCompact;
  /** Proto metric values are ~14px; titleLg (22) truncates in 2-col mobile. */
  const metricCompact = !wide;

  return (
    <View style={styles.shell}>
      <View style={[styles.head, !wide && styles.headerStack]}>
        <View style={[styles.headerTitleBlock, wide && styles.headerTitleBlockWide]}>
          <Text style={styles.title}>Resumen fiscal</Text>
          {validatedHint ? (
            <Text style={styles.validatedMeta}>Conectada el {validatedHint}</Text>
          ) : null}
        </View>
        {/*
          Compact: columna (no row+wrap). En Yoga/Android el wrap+gap achicaba el Text
          a "PV…" en vez de pasar el 2º pill abajo; web sí wrapeaba a la línea siguiente.
        */}
        <View style={[styles.headerPills, !wide && styles.headerPillsCompact]}>
          {orgStatusPill}
          {branchFiscal ? pvStatusPill : null}
          {certExpiryPill}
        </View>
      </View>

      <EmbeddedMetricGrid
        wrapStyle={styles.metricsWrap}
        gridStyle={wide ? styles.metaGridWide : styles.metaGridCompact}
      >
        <EmbeddedMetric
          label="Régimen"
          value={spanishTaxRegimeLabel(profile?.taxRegime)}
          compact={metricCompact}
          minWidth={0}
          style={metricCell}
        />
        {profile?.emitterCuit ? (
          <EmbeddedMetric
            label="CUIT emisor"
            value={formatCuitDisplay(profile.emitterCuit)}
            compact={metricCompact}
            minWidth={0}
            style={metricCell}
          />
        ) : null}
        {isMono && profile?.monotributoCategory ? (
          <EmbeddedMetric
            label="Categoría"
            value={profile.monotributoCategory}
            compact={metricCompact}
            minWidth={0}
            style={metricCell}
          />
        ) : null}
        {wide && profile?.businessName ? (
          <EmbeddedMetric
            label="Razón social"
            value={profile.businessName}
            minWidth={0}
            compact
            style={metricCell}
          />
        ) : null}
        {profile?.domicilioComercial ? (
          <EmbeddedMetric
            label="Domicilio"
            value={profile.domicilioComercial}
            compact={metricCompact}
            minWidth={0}
            style={metricCell}
          />
        ) : null}
        {profile?.iibb ? (
          <EmbeddedMetric
            label="Ingresos Brutos"
            value={profile.iibb}
            compact={metricCompact}
            minWidth={0}
            style={metricCell}
          />
        ) : null}
        {profile?.inicioActividades ? (
          <EmbeddedMetric
            label="Inicio de actividades"
            value={formatIsoDateLabelEsAr(profile.inicioActividades)}
            compact={metricCompact}
            minWidth={0}
            style={metricCell}
          />
        ) : null}
        {branchFiscal?.pointOfSale != null ? (
          <EmbeddedMetric
            label="Punto de venta"
            value={String(branchFiscal.pointOfSale)}
            compact={metricCompact}
            minWidth={0}
            style={metricCell}
          />
        ) : null}
        {branchFiscal?.pvLastInvoiceNumber != null ? (
          <EmbeddedMetric
            label="Último ARCA"
            value={String(branchFiscal.pvLastInvoiceNumber)}
            compact={metricCompact}
            minWidth={0}
            style={metricCell}
          />
        ) : null}
        {profile?.environment ? (
          <EmbeddedMetric
            label="Ambiente"
            value={spanishArcaEnvironmentLabel(profile.environment)}
            compact={metricCompact}
            minWidth={0}
            style={metricCell}
          />
        ) : null}
      </EmbeddedMetricGrid>

      {ytdTotal != null ? (
        <View style={styles.ytdPanel}>
          <Text style={styles.ytdKicker}>
            Facturado fiscal {new Date().getFullYear()}
            {isMono ? ' · de todo el negocio' : ''}
          </Text>
          <View style={styles.ytdHeroRow}>
            <Text style={styles.ytdHeroValue}>{formatYtdMoney(ytdTotal)}</Text>
            {limit != null ? (
              <Text style={styles.ytdHeroLimit}>de {formatYtdMoney(limit)}</Text>
            ) : null}
          </View>
          {ytdBranchTotal != null && ytdBranchTotal !== ytdTotal ? (
            <Text style={styles.ytdBranchMeta}>Esta sucursal: {formatYtdMoney(ytdBranchTotal)}</Text>
          ) : null}
          {limitPct != null ? (
            <>
              <View style={styles.progressTrack}>
                <View
                  style={[
                    styles.progressFill,
                    { width: `${Math.max(limitPct, limitPct > 0 ? 2 : 0)}%` },
                    limitPct > 85 ? styles.progressFillWarn : null,
                  ]}
                />
              </View>
              <Text style={styles.ytdPctMeta}>
                {limitPct.toLocaleString('es-AR')}% del tope anual
              </Text>
            </>
          ) : null}
          {isMono && profile?.monotributoCategory ? (
            <Text style={styles.ytdFooterLine}>
              Tope categoría {profile.monotributoCategory} ·{' '}
              <Text
                style={styles.ytdFooterLink}
                onPress={() => void Linking.openURL(MONOTRIBUTO_OFFICIAL_CATEGORIES_URL)}
                accessibilityRole="link"
              >
                Consultar escalas en ARCA
              </Text>
            </Text>
          ) : null}
        </View>
      ) : null}

      {profile?.setupError ? <Text style={styles.errorText}>{profile.setupError}</Text> : null}
      {branchFiscal?.pvSetupError ? <Text style={styles.errorText}>{branchFiscal.pvSetupError}</Text> : null}

      {showActions && profile?.environment === 'homologacion' && onActivateProduction ? (
        <View style={[styles.cutoverBanner, wide && styles.cutoverBannerWide]}>
          <Text style={styles.cutoverText}>Estás en modo prueba. Estos comprobantes no valen.</Text>
          <TouchableOpacity
            style={[settingsStyles.primaryButton, wide && styles.cutoverCtaWide]}
            onPress={onActivateProduction}
            accessibilityRole="button"
            accessibilityLabel="Facturar de verdad"
          >
            <Text style={settingsStyles.primaryButtonText}>Facturar de verdad</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {showActions &&
      (onEditConfiguration || onViewAllBranches || onChangeRegime || (onExportLibroIva && profile?.taxRegime === 'responsable_inscripto' && branchId)) ? (
        <View style={[styles.actions, wide && styles.actionsWide]}>
          {onEditConfiguration ? (
            <TouchableOpacity
              style={[settingsStyles.secondaryButton, wide && styles.actionBtnWide]}
              onPress={onEditConfiguration}
            >
              <Text style={settingsStyles.secondaryButtonText}>Editar configuración</Text>
            </TouchableOpacity>
          ) : null}
          {onViewAllBranches ? (
            <TouchableOpacity
              style={[styles.tertiaryButton, wide && styles.actionBtnWide]}
              onPress={onViewAllBranches}
            >
              <Text style={styles.tertiaryButtonText}>Ver todas las sucursales</Text>
            </TouchableOpacity>
          ) : null}
          {onChangeRegime ? (
            <TouchableOpacity
              style={[styles.tertiaryButton, wide && styles.actionBtnWide]}
              onPress={onChangeRegime}
            >
              <Text style={styles.tertiaryButtonText}>Cambiar régimen</Text>
            </TouchableOpacity>
          ) : null}
          {onExportLibroIva && profile?.taxRegime === 'responsable_inscripto' && branchId ? (
            <TouchableOpacity
              style={[styles.tertiaryButton, wide && styles.actionBtnWide]}
              onPress={onExportLibroIva}
              testID="fiscal.status.export-iva"
            >
              <Text style={styles.tertiaryButtonText}>Exportar libro IVA</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    alignSelf: 'stretch',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
    gap: 0,
  },
  loadingShell: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    paddingVertical: spacing.xl,
    alignItems: 'center',
  },
  head: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.sm,
    flexWrap: 'wrap',
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
    // Native: sin esto, pills más anchos que la card se dibujan fuera y `shell.overflow:hidden` los corta.
    // Web suele expandir el hijo; por eso el bug solo se veía en Android/iOS.
    width: '100%',
    maxWidth: '100%',
  },
  headerStack: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  headerTitleBlock: {
    minWidth: 0,
    gap: 2,
  },
  headerTitleBlockWide: {
    flex: 1,
  },
  title: {
    fontSize: 16,
    fontWeight: '800',
    letterSpacing: -0.02 * 16,
    color: ticketColors.ink,
  },
  headerPills: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    alignItems: 'center',
    maxWidth: '100%',
  },
  headerPillsCompact: {
    alignSelf: 'stretch',
    width: '100%',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
  },
  validatedMeta: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '600',
    fontSize: 12,
  },
  metricsWrap: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  metaGridWide: {
    gap: spacing.md,
  },
  /** Proto phone: `grid-template-columns: 1fr 1fr; gap: 12px 14px`. */
  metaGridCompact: {
    gap: 12,
    rowGap: 12,
    columnGap: 14,
  },
  metricCellWide: {
    flex: 0,
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: '31%',
    maxWidth: '31%',
    minWidth: 0,
  },
  metricCellCompact: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: '42%',
    maxWidth: '48%',
    minWidth: 0,
  },
  ytdPanel: {
    marginHorizontal: spacing.md,
    marginBottom: spacing.md,
    padding: spacing.md,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    gap: spacing.sm,
    alignSelf: 'stretch',
    minWidth: 0,
  },
  ytdKicker: {
    fontSize: 11,
    fontWeight: '800',
    color: ticketColors.ink3,
    letterSpacing: 0.06 * 11,
    textTransform: 'uppercase',
  },
  ytdHeroRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: spacing.sm,
    width: '100%',
    minWidth: 0,
  },
  ytdHeroValue: {
    fontSize: 26,
    lineHeight: 30,
    fontWeight: '800',
    letterSpacing: -0.03 * 26,
    color: ticketColors.ink,
    fontVariant: ['tabular-nums'],
    flexShrink: 1,
    minWidth: 0,
  },
  ytdHeroLimit: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink3,
    fontVariant: ['tabular-nums'],
    flexShrink: 1,
    minWidth: 0,
  },
  ytdBranchMeta: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontVariant: ['tabular-nums'],
  },
  progressTrack: {
    height: 6,
    borderRadius: 99,
    backgroundColor: ticketColors.rule,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: ticketColors.gold,
    borderRadius: 99,
  },
  progressFillWarn: {
    backgroundColor: colors.warning,
  },
  ytdPctMeta: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink3,
    fontVariant: ['tabular-nums'],
  },
  ytdFooterLine: {
    fontSize: 12,
    lineHeight: 18,
    fontWeight: '500',
    color: ticketColors.ink3,
  },
  ytdFooterLink: {
    fontSize: 12,
    lineHeight: 18,
    fontWeight: '700',
    color: colors.accent,
  },
  errorText: {
    ...typography.caption,
    color: ticketColors.bad,
    lineHeight: 18,
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.sm,
  },
  cutoverBanner: {
    marginHorizontal: spacing.md,
    marginBottom: spacing.md,
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(180, 83, 9, 0.25)',
    backgroundColor: 'rgba(180, 83, 9, 0.08)',
  },
  cutoverBannerWide: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  cutoverText: {
    ...typography.body,
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '600',
    color: colors.warning,
  },
  cutoverCtaWide: {
    flexGrow: 0,
    flexShrink: 0,
    alignSelf: 'center',
    paddingHorizontal: spacing.lg,
  },
  actions: {
    gap: spacing.sm,
    alignSelf: 'stretch',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
  },
  actionsWide: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  actionBtnWide: {
    flex: 1,
    minWidth: 160,
  },
  tertiaryButton: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    minHeight: 48,
  },
  tertiaryButtonText: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
  },
});
